/**
 * Money formatting helpers.
 *
 * All business values are bigint minor units (e.g. 10050n = ฿100.50).
 * These helpers are display-only — never use them in calculations.
 */

// Pinned against `@b1fr0st/shared/money` by `shared-exponent-parity.test.ts`:
// the display rate converts with the shared exponents, so a code missing here
// would show a converted figure under the wrong decimal point.
const ZERO_DP_CURRENCIES = ['JPY', 'KRW', 'VND', 'LAK', 'CLP', 'PYG', 'RWF', 'XAF', 'XOF'];
const THREE_DP_CURRENCIES = ['TND', 'BHD', 'KWD', 'OMR', 'JOD', 'IQD', 'LYD'];

function divisorFor(currency: string): number {
  if (ZERO_DP_CURRENCIES.includes(currency.toUpperCase())) return 1;
  if (THREE_DP_CURRENCIES.includes(currency.toUpperCase())) return 1000;
  return 100;
}

function dpFor(currency: string): number {
  if (ZERO_DP_CURRENCIES.includes(currency.toUpperCase())) return 0;
  if (THREE_DP_CURRENCIES.includes(currency.toUpperCase())) return 3;
  return 2;
}

/**
 * Returns the minor-unit multiplier for the given currency code.
 * THB/USD/EUR → 100n, JPY/KRW → 1n, TND/BHD → 1000n.
 */
export function multiplierFor(currency: string): bigint {
  return BigInt(divisorFor(currency));
}

export type ParsedMinor = { ok: true; minor: bigint } | { ok: false; reason: string };

/**
 * The only shape accepted: optional sign, digits, optional single decimal part.
 *
 * Deliberately narrower than `BigInt()`, which accepts `0x10` as 16 — a player
 * typing that into an amount box was charged sixteen. Exponent forms are
 * excluded too: `<Input type="number">` reports `1e3` as a valid value, so it
 * arrives through a field that looks like it is already validating.
 */
const DECIMAL = /^[+-]?\d+(?:\.\d+)?$/;

/**
 * Parse a decimal string into minor units, reporting failure instead of
 * throwing. Every render path must use this — a label is never worth an
 * exception (WEB-22).
 */
export function tryToMinorUnits(value: string | number, currency: string): ParsedMinor {
  if (typeof value === 'number' && !Number.isFinite(value)) {
    return { ok: false, reason: 'not a finite number' };
  }
  const str = String(value).trim();
  if (!str) return { ok: false, reason: 'empty' };
  if (str.startsWith('+') || !DECIMAL.test(str)) {
    return { ok: false, reason: 'not a decimal amount' };
  }

  const negative = str.startsWith('-');
  const [intPart = '', fracPart = ''] = str.replace(/^-/, '').split('.');
  const dp = dpFor(currency);
  // Truncate toward zero past the currency's precision, in both directions —
  // never inflate a balance (CLAUDE.md §2.5).
  const fracPadded = fracPart.padEnd(dp, '0').slice(0, dp);
  const magnitude = BigInt(intPart) * BigInt(divisorFor(currency)) + BigInt(fracPadded || '0');
  return { ok: true, minor: negative ? -magnitude : magnitude };
}

/**
 * Convert a decimal string or number to minor units (bigint).
 * E.g. toMinorUnits("100.50", "THB") → 10050n
 *
 * Throws on anything it cannot parse, so a submit path can never quietly send
 * zero. Call it only where a throw is contained; anywhere inside a render or a
 * validator, use `tryToMinorUnits`.
 */
export function toMinorUnits(value: string | number, currency: string): bigint {
  const parsed = tryToMinorUnits(value, currency);
  if (!parsed.ok) throw new Error(`invalid amount "${String(value)}": ${parsed.reason}`);
  return parsed.minor;
}

/**
 * A minor-unit figure off the wire as the bigint the money helpers compute in,
 * or `null` when it is not one.
 *
 * The API serialises money as integer minor units in a JSON number (a bigint
 * cannot be JSON). A fraction there is a broken reply, not an amount to round,
 * so it is never truncated into a plausible figure — and never thrown either:
 * this is reached from render, where an exception blanks the screen (WEB-22).
 * The caller decides what an unreadable figure shows.
 */
export function minorFromWire(value: bigint | number): bigint | null {
  if (typeof value === 'bigint') return value;
  return Number.isSafeInteger(value) ? BigInt(value) : null;
}

/** Decimal places this currency uses — 0 for JPY/KRW, 3 for the Gulf dinars. */
export function decimalPlacesFor(currency: string): number {
  return dpFor(currency);
}

/**
 * What an amount box typed or pasted into should be read as.
 *
 * Whitespace only. A grouping separator is deliberately NOT stripped: the comma
 * is the DECIMAL separator in several of the languages this app ships, so
 * reading "500,00" as 50000 would take a hundred times what the player typed.
 * An amount carrying one is refused and named, never guessed at.
 */
export function normalizeAmountInput(value: string): string {
  return value.trim();
}

/**
 * Minor units as a plain decimal string — no symbol, no grouping.
 *
 * For putting a value back INTO an amount input, where `formatMoney` would
 * insert a currency symbol and thousands separators that the field's own
 * validation then rejects.
 */
export function minorToPlain(minor: bigint | number, currency: string): string {
  const n = typeof minor === 'bigint' ? minor : BigInt(Math.round(minor));
  const divisor = BigInt(divisorFor(currency));
  const dp = dpFor(currency);
  const whole = n / divisor;
  if (dp === 0) return String(whole);
  const frac = (n < 0n ? -n : n) % divisor;
  return `${whole}.${String(frac).padStart(dp, '0')}`;
}

const CURRENCY_GLYPH: Record<string, string> = {
  THB: '฿',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  KRW: '₩',
  VND: '₫',
  LAK: '₭',
};

/** Symbol for an amount-input prefix. Falls back to the first two letters. */
export function currencyGlyph(code: string): string {
  return CURRENCY_GLYPH[code.toUpperCase()] ?? code.slice(0, 2).toUpperCase();
}

/**
 * Convert minor units to the plain decimal string an amount input expects —
 * no grouping separators, no currency symbol, trailing zeros trimmed.
 * E.g. toDecimalString(10050n, "THB") → "100.5", toDecimalString(1234n, "JPY") → "1234"
 */
export function toDecimalString(minor: bigint | number, currency = 'THB'): string {
  const value = typeof minor === 'bigint' ? minor : BigInt(Math.trunc(minor));
  const divisor = BigInt(divisorFor(currency));
  const dp = dpFor(currency);
  const sign = value < 0n ? '-' : '';
  const abs = value < 0n ? -value : value;
  if (dp === 0) return `${sign}${abs}`;
  const frac = (abs % divisor).toString().padStart(dp, '0').replace(/0+$/, '');
  return `${sign}${abs / divisor}${frac ? `.${frac}` : ''}`;
}

/**
 * Format minor-unit amount as a locale-aware currency string.
 *
 * @param minor  - bigint or plain number of minor units (e.g. 10050n or 10050)
 * @param currency - ISO 4217 code (default: 'THB')
 */
export function formatMoney(minor: bigint | number, currency = 'THB'): string {
  const n = typeof minor === 'bigint' ? Number(minor) : minor;
  const divisor = divisorFor(currency);
  const dp = dpFor(currency);
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: dp,
  }).format(n / divisor);
}
